import { defineComponent, reactive, ref, toRefs, watch } from "vue";
import { api } from "~/api";
import { MsgBox } from "~/components/MsgBox";
import { Log } from "~/extensions/utils";
import { useRouter } from "vue-router";
import { store } from "~/pages/taikyo/store/TaikyoStore";
import { myPageStore } from "~/store/MyPageStore";
import { TaikyoEntryPageViewModel } from "./Models/TaikyoEntryPageViewModel";
import { TaikyoEntryPageValidator } from "./Models/TaikyoEntryPageValidator";
import { commonStore } from "~/store/CommonStore";
import { PageID, TaikyoRiyuKb } from "~/extensions/utils/DefineTypes";
import { createAddress } from "~/extensions/utils/AddressHelper";
import BreadcrumbList from "~/components/BreadcrumbList.vue";
import dayjs from "dayjs";
import { updateKaiyakuYmd } from "../notification/Models/TaikyoFormHelper";
const log = Log.create("TaikyoEntryPage");
export default defineComponent({
    name: "TaikyoEntryPage",
    components: {
        BreadcrumbList
    },
    beforeRouteEnter(to, from, next) {
        const fromScreen = from.name;
        const relatedScreens = ["taikyo-precaution", "taikyo-entry-check"];
        if (relatedScreens.includes(fromScreen) && store.privacyConsent) {
            next();
            return;
        }
        next({ name: "taikyo" });
    },
    async setup() {
        // 画面名取得
        const pageName = commonStore.pageName(PageID.taikyoEntry);
        const router = useRouter();
        const html = ref("");
        const isMultipleKeiyaku = myPageStore.isMultipleKeiyaku;
        const personal = reactive(myPageStore.personal);
        const taikyo = reactive(store.taikyoEntity);
        const taikyoForInput = reactive(store.taikyoForInput);
        const taikyoEntryPageViwModel = new TaikyoEntryPageViewModel(store.taikyoEntity, myPageStore.selectedNyukyokeiyaku.roomSb);
        const validator = reactive(new TaikyoEntryPageValidator(taikyoEntryPageViwModel, taikyoForInput));
        const movingServicesList = [
            { label: "同意します", value: 1 },
            { label: "同意しません", value: 0 }
        ];
        //初回遷移時に連絡先を初期セット
        if (typeof taikyo.tel === "undefined" && typeof taikyo.email === "undefined") {
            taikyo.tel = personal.telephoneNumber;
            taikyo.email = personal.email;
            taikyo.taikyoYmd = null;
        }
        // 初期表示処理
        const initView = async () => {
            try {
                html.value = await api.taikyo.loadEntryprecautionsText();
                await store.loadForTaikyo();
            }
            catch (ex) {
                log.error("setup", ex);
                MsgBox.error(ex?.message ?? "エラーが発生しました。");
                router.push({ name: "top" });
            }
        };
        const getAddress = () => {
            const selected = myPageStore.selectedNyukyokeiyaku;
            return [selected?.postCode ? `〒${selected.postCode}` : "", selected?.adr1, selected?.adr2, selected?.roomNumber ? `${selected.bukkenName} ${selected.roomNumber}` : ""]
                .filter(Boolean)
                .join("\r\n");
        };
        const formatTel = () => {
            if (validator.tel.error || taikyo.tel.length === 13) {
                return;
            }
            const value = taikyo.tel.replace("-", "");
            taikyo.tel = `${value.substring(0, 3)}-${value.substring(3, 7)}-${value.substring(7)}`;
        };
        const setDate = () => {
            const taikyoYmd = taikyo.taikyoYmd;
            const today = dayjs().startOf("day");
            const monthLater = today.add(1, "month");
            if (!taikyoYmd || !dayjs(taikyoYmd).isValid()) {
                taikyo.kaiyakuYmd = null;
            }
            else if (monthLater.isAfter(taikyoYmd)) {
                taikyo.kaiyakuYmd = dayjs(taikyoYmd).date() > monthLater.date() ? monthLater.toDate() : monthLater.subtract(1, "day").toDate();
            }
            else {
                taikyo.kaiyakuYmd = dayjs(taikyoYmd).toDate();
            }
        };
        const findAddress = async () => {
            if (!taikyo.postCode || validator.postCode.error) {
                return;
            }
            if (taikyo.postCode.length === 7) {
                taikyo.postCode = `${taikyo.postCode.substring(0, 3)}-${taikyo.postCode.substring(3)}`;
            }
            try {
                if (/^\d{3}-\d{4}$/.test(taikyo.postCode)) {
                    taikyo.adr1 = await createAddress(taikyo.postCode);
                }
            }
            catch (ex) {
                if (ex?.status !== 404) {
                    log.error("findAddress", ex);
                }
            }
        };
        const formatAddress = () => {
            return [taikyo.postCode ? `〒${taikyo.postCode}` : "", taikyo.adr1, taikyo.adr2, taikyo.adr3].filter(Boolean).join("\r\n");
        };
        const clearTaikyoRiyu = () => {
            if (taikyoEntryPageViwModel.isDisableTaikyoRiyu) {
                taikyo.taikyoRiyu = null;
            }
        };
        const submit = () => {
            if (validator.error) {
                MsgBox.error(commonStore.messages.PV0017);
                return false;
            }
            // ハイフン対応
            if (typeof taikyo.postCode !== "undefined" && taikyo.postCode !== null && taikyo.postCode !== "" && taikyo.postCode.length === 7) {
                taikyo.postCode = taikyo.postCode.substring(0, 3) + "-" + taikyo.postCode.substring(3);
            }
            //ハイフン対応
            if (taikyo.tel.length !== 13) {
                const temp = taikyo.tel.replace("-", "");
                taikyo.tel = temp.substring(0, 3) + "-" + temp.substring(3, 7) + "-" + temp.substring(7);
            }
            taikyo.addressAll = formatAddress();
            store.setData(taikyo, taikyoForInput);
            return true;
        };
        /** 確認へボタン押下イベント */
        const confirmPush = () => {
            const isNotError = submit();
            if (isNotError) {
                router.push({ name: "taikyo-entry-check" });
            }
        };
        watch(() => taikyo.taikyoYmd, () => {
            updateKaiyakuYmd(taikyo);
        });
        await initView();
        return {
            ...toRefs(store),
            html,
            personal,
            taikyo,
            taikyoForInput,
            confirmPush,
            validator,
            taikyoEntryPageViwModel,
            TaikyoRiyuKb,
            movingServicesList,
            isMultipleKeiyaku,
            getAddress,
            pageName,
            PageID,
            formatTel,
            findAddress,
            clearTaikyoRiyu,
            myPageStore,
            dayjs,
            router,
            setDate
        };
    }
});
